import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAllCollectionsAsync } from '../utils/inventoryService';
import FavoriteButton from './FavoriteButton';
import LazyImage from './LazyImage';

const SearchContainer = styled.div`
  padding: 40px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ccc;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ff6b6b;
  font-size: 1rem;
  gap: 15px;
`;

const RetryButton = styled.button`
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #555;
  }
`;

const SearchHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 20px;
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-top: -15px;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  font-size: 1rem;
  min-width: 0;
  
  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const SearchButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #36964f;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 25px;
`;

const FilterSectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 15px;
  font-weight: 500;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FilterGroups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const FilterRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
`;

const FilterGroupTitle = styled.div`
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 5px;
  color: #ddd;
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;

  input {
    margin-right: 10px;
  }

  label {
    font-size: 0.9rem;
    cursor: pointer;
  }
`;

const FilterButton = styled.button`
  background: ${props => props.disabled ? '#555' : '#2c7d46'};
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  align-self: flex-start;
  margin-top: 10px;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background: ${props => props.disabled ? '#555' : '#36964f'};
  }
`;

const ResultsInfo = styled.div`
  margin-bottom: 20px;
  font-size: 0.9rem;
  color: #aaa;
`;

const NoResultsMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 1.1rem;
  color: #aaa;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
`;

const ResultCard = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ResultImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
`;

const ResultImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${ResultCard}:hover & {
    transform: scale(1.05);
  }
`;

const ResultDetails = styled.div`
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ResultTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const CollectionName = styled(Link)`
  font-size: 0.8rem;
  margin-bottom: 10px;
  color: #aaa;
  
  &:hover {
    color: #fff;
  }
`;

const ResultInfo = styled.div`
  margin-bottom: 10px;
  flex-grow: 1;
`;

const ResultDetail = styled.div`
  font-size: 0.9rem;
  color: #ddd;
  margin-bottom: 5px;
`;


const ResultStatus = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #a00;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
`;

const ResultAvailable = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #2c7d46;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
`;

function SearchPage() {
  const [filteredResults, setFilteredResults] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [filterMaterial, setFilterMaterial] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterPriceRange, setFilterPriceRange] = useState('');

  // Load all items on component mount
  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        setLoading(true);
        setError(null);

        const collections = await getAllCollectionsAsync();
        const items = [];

        collections.forEach(collection => {
          if (collection.items && Array.isArray(collection.items)) {
            collection.items.forEach(item => {
              // Add collection info to each item
              items.push({
                ...item,
                collectionId: collection.id,
                collectionTitle: collection.title,
                collectionFolder: collection.folder
              });
            });
          }
        });

        if (mounted) {
          setAllItems(items);
        }
      } catch (err) {
        console.error('Error loading items:', err);
        if (mounted) {
          setError('Failed to load items. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      mounted = false;
    };
  }, []);
  
  // Get image path for an item
  const getImagePath = (item) => {
    // Items can have either 'image' or 'imagePath' property
    const imagePath = item.image || item.imagePath;

    if (!imagePath) {
      console.warn('Missing image path for item:', item.id);
      return '';
    }

    // If it's already a full path, use it directly
    if (imagePath.includes('/')) {
      return imagePath;
    }

    // Otherwise build the path
    return `/images/collections/${item.collectionFolder}/${imagePath}`;
  };

  // Helper function to check if an item matches the size filter
  const matchesSize = (item, sizeFilter) => {
    if (!sizeFilter || !item.size) return true;

    // Extract dimensions from size string
    const dimensions = item.size.match(/\d+/g);
    if (!dimensions || dimensions.length < 2) return false;

    const width = parseInt(dimensions[0], 10);
    const height = parseInt(dimensions[1], 10);
    const area = width * height;

    switch(sizeFilter) {
      case 'small':
        return width <= 24 && height <= 24;
      case 'medium':
        return (width > 24 || height > 24) && (width <= 36 || height <= 48);
      case 'large':
        return width > 36 || height > 48;
      default:
        return true;
    }
  };

  // Helper function to check if an item matches the price filter
  const matchesPrice = (item, priceFilter) => {
    if (!priceFilter || !item.price) return true;

    const price = item.price;

    switch(priceFilter) {
      case 'under1000':
        return price < 1000;
      case '1000to2500':
        return price >= 1000 && price <= 2500;
      case '2500to5000':
        return price > 2500 && price <= 5000;
      case 'over5000':
        return price > 5000;
      default:
        return true;
    }
  };

  // Helper function to check if an item matches the material filter
  const matchesMaterial = (item, materialFilter) => {
    if (!materialFilter || !item.medium) return true;

    const medium = item.medium.toLowerCase();

    switch(materialFilter) {
      case 'encaustic':
        return medium.includes('encaustic');
      case 'acrylicCanvas':
        return medium.includes('acrylic') && medium.includes('canvas');
      case 'acrylicPaper':
        return medium.includes('acrylic') && medium.includes('paper');
      case 'acrylicPen':
        return medium.includes('acrylic pen');
      default:
        return true;
    }
  };
  
  // Check if at least one filter is selected
  const isAnyFilterSelected = filterMaterial || filterSize || filterPriceRange;

  // Handle filter button click
  const handleFilterClick = () => {
    const results = allItems.filter(item => {
      return (
        item.available === true && // Only include available items
        matchesMaterial(item, filterMaterial) &&
        matchesSize(item, filterSize) &&
        matchesPrice(item, filterPriceRange)
      );
    });

    setFilteredResults(results);
    setHasFiltered(true);
  };

  // Handle radio button changes
  const handleMaterialChange = (value) => {
    setFilterMaterial(value);
  };

  const handleSizeChange = (value) => {
    setFilterSize(value);
  };

  const handlePriceRangeChange = (value) => {
    setFilterPriceRange(value);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <SearchContainer>
        <LoadingContainer>Loading artwork...</LoadingContainer>
      </SearchContainer>
    );
  }

  if (error) {
    return (
      <SearchContainer>
        <ErrorContainer>
          <div>{error}</div>
          <RetryButton onClick={handleRetry}>Retry</RetryButton>
        </ErrorContainer>
      </SearchContainer>
    );
  }

  // Filter default display to only show available items
  const availableItems = allItems.filter(item => item.available === true);

  return (
    <SearchContainer>
      <SearchHeader>Find Something Perfect for Me</SearchHeader>

      <FilterContainer>
        <FilterSectionTitle>Filter Artwork</FilterSectionTitle>

        <FilterGroups>
          <FilterRadioGroup>
            <FilterGroupTitle>Material</FilterGroupTitle>

            <FilterOption>
              <input
                type="radio"
                id="material-all"
                name="material"
                value=""
                checked={filterMaterial === ''}
                onChange={() => handleMaterialChange('')}
              />
              <label htmlFor="material-all">All Materials</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="material-encaustic"
                name="material"
                value="encaustic"
                checked={filterMaterial === 'encaustic'}
                onChange={() => handleMaterialChange('encaustic')}
              />
              <label htmlFor="material-encaustic">Encaustic</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="material-acrylicCanvas"
                name="material"
                value="acrylicCanvas"
                checked={filterMaterial === 'acrylicCanvas'}
                onChange={() => handleMaterialChange('acrylicCanvas')}
              />
              <label htmlFor="material-acrylicCanvas">Acrylic on Canvas</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="material-acrylicPaper"
                name="material"
                value="acrylicPaper"
                checked={filterMaterial === 'acrylicPaper'}
                onChange={() => handleMaterialChange('acrylicPaper')}
              />
              <label htmlFor="material-acrylicPaper">Acrylic on Paper</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="material-acrylicPen"
                name="material"
                value="acrylicPen"
                checked={filterMaterial === 'acrylicPen'}
                onChange={() => handleMaterialChange('acrylicPen')}
              />
              <label htmlFor="material-acrylicPen">Acrylic Pen</label>
            </FilterOption>
          </FilterRadioGroup>

          <FilterRadioGroup>
            <FilterGroupTitle>Size</FilterGroupTitle>

            <FilterOption>
              <input
                type="radio"
                id="size-all"
                name="size"
                value=""
                checked={filterSize === ''}
                onChange={() => handleSizeChange('')}
              />
              <label htmlFor="size-all">All Sizes</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="size-small"
                name="size"
                value="small"
                checked={filterSize === 'small'}
                onChange={() => handleSizeChange('small')}
              />
              <label htmlFor="size-small">Small (up to 24" x 24")</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="size-medium"
                name="size"
                value="medium"
                checked={filterSize === 'medium'}
                onChange={() => handleSizeChange('medium')}
              />
              <label htmlFor="size-medium">Medium (24" x 36" to 36" x 48")</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="size-large"
                name="size"
                value="large"
                checked={filterSize === 'large'}
                onChange={() => handleSizeChange('large')}
              />
              <label htmlFor="size-large">Large (larger than 36" x 48")</label>
            </FilterOption>
          </FilterRadioGroup>

          <FilterRadioGroup>
            <FilterGroupTitle>Price Range</FilterGroupTitle>

            <FilterOption>
              <input
                type="radio"
                id="price-all"
                name="price"
                value=""
                checked={filterPriceRange === ''}
                onChange={() => handlePriceRangeChange('')}
              />
              <label htmlFor="price-all">All Prices</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="price-under1000"
                name="price"
                value="under1000"
                checked={filterPriceRange === 'under1000'}
                onChange={() => handlePriceRangeChange('under1000')}
              />
              <label htmlFor="price-under1000">Under $1,000</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="price-1000to2500"
                name="price"
                value="1000to2500"
                checked={filterPriceRange === '1000to2500'}
                onChange={() => handlePriceRangeChange('1000to2500')}
              />
              <label htmlFor="price-1000to2500">$1,000 - $2,500</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="price-2500to5000"
                name="price"
                value="2500to5000"
                checked={filterPriceRange === '2500to5000'}
                onChange={() => handlePriceRangeChange('2500to5000')}
              />
              <label htmlFor="price-2500to5000">$2,500 - $5,000</label>
            </FilterOption>

            <FilterOption>
              <input
                type="radio"
                id="price-over5000"
                name="price"
                value="over5000"
                checked={filterPriceRange === 'over5000'}
                onChange={() => handlePriceRangeChange('over5000')}
              />
              <label htmlFor="price-over5000">Over $5,000</label>
            </FilterOption>
          </FilterRadioGroup>
        </FilterGroups>

        <FilterButton
          onClick={handleFilterClick}
          disabled={!isAnyFilterSelected}
        >
          Go
        </FilterButton>
      </FilterContainer>

      {hasFiltered ? (
        <ResultsInfo>
          Found {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'} available for purchase
        </ResultsInfo>
      ) : (
        <ResultsInfo>
          Showing {availableItems.length} items available for purchase
        </ResultsInfo>
      )}

      {hasFiltered && filteredResults.length === 0 ? (
        <NoResultsMessage>
          No results found matching your filters. Try different filter options.
        </NoResultsMessage>
      ) : (
        <ResultsGrid>
          {(hasFiltered ? filteredResults : availableItems).map(item => (
            <ResultCard key={item.id}>
              <ResultImageContainer>
                <LazyImage
                  src={getImagePath(item)}
                  alt={item.title}
                  hoverEffect={true}
                />
                <FavoriteButton item={item} />
                {item.available ? (
                  <ResultAvailable>Available</ResultAvailable>
                ) : item.status ? (
                  <ResultStatus>{item.status}</ResultStatus>
                ) : null}
              </ResultImageContainer>
              
              <ResultDetails>
                <ResultTitle>
                  {item.title}
                </ResultTitle>
                <CollectionName to={`/collection/${item.collectionId}`}>
                  {item.collectionTitle}
                </CollectionName>

                <ResultInfo>
                  {item.medium && (
                    <ResultDetail>
                      Medium: <strong>{item.medium}</strong>
                    </ResultDetail>
                  )}
                  {item.size && (
                    <ResultDetail>
                      Size: <strong>{item.size}</strong>
                    </ResultDetail>
                  )}
                  {item.year && (
                    <ResultDetail>
                      Year: <strong>{item.year.toString()}</strong>
                    </ResultDetail>
                  )}
                  {item.available && item.price && (
                    <ResultDetail>
                      Price: <strong>${item.price.toLocaleString()}</strong>
                    </ResultDetail>
                  )}
                </ResultInfo>
                
                <Link to={`/collection/${item.collectionId}`} style={{ alignSelf: 'flex-start' }}>
                  <SearchButton>View in Collection</SearchButton>
                </Link>
              </ResultDetails>
            </ResultCard>
          ))}
        </ResultsGrid>
      )}
    </SearchContainer>
  );
}

export default SearchPage;